/**
 * The remembered fold of Home's sidebar groups (Recents, Apps, Spaces), kept
 * per group in localStorage.
 *
 * `HomeSidebarMore` is the "··· More" row under Command center: opened, it
 * gives its place to the occasional pages (Skills, Knowledge) and a
 * "Show less" row, so they stay one click away without lengthening the
 * sidebar.
 *
 * @module components/home/HomeSidebarSection
 */
import * as Schema from "effect/Schema";
import { ChevronUpIcon, EllipsisIcon } from "lucide-react";
import { type ReactNode, useEffect, useId, useRef } from "react";

import { useLocalStorage } from "../../hooks/useLocalStorage";
import { TIMELINE_DISCLOSURE_PANEL_CLASS } from "../chat/timelineDisclosure";
import { Collapsible, CollapsiblePanel } from "../ui/collapsible";
import { SidebarMenu, SidebarMenuItem } from "../ui/sidebar";
import { SidebarRow } from "../ui/sidebar-row";

/** Where a group's folded state is kept. */
export const homeSidebarSectionStorageKey = (id: string) => `home_sidebar_section_collapsed:${id}`;

/** A group's remembered open state; `forceOpen` shows it open without changing what is remembered. */
export function useHomeSidebarSectionOpen(id: string, forceOpen = false): [boolean, (open: boolean) => void] {
  const [collapsed, setCollapsed] = useLocalStorage(homeSidebarSectionStorageKey(id), false, Schema.Boolean);
  return [!collapsed || forceOpen, (open) => setCollapsed(!open)];
}

/**
 * "··· More": a row in the top list that gives its place to the pages it
 * hides. Opened, the More row folds away and its rows (Skills, Knowledge)
 * unfold in the same list, followed by "Show less", which folds them back
 * and brings More back. Both travel on the timeline's height-and-opacity
 * ease (still under reduced motion). Closed until opened, the choice is
 * remembered like the groups', and it stays open while one of its pages
 * shows (`holdOpen`) so the selected row is never folded away; Show less is
 * left out then, since it could not fold.
 *
 * `children` are the revealed `SidebarMenuItem`s.
 */
export function HomeSidebarMore({
  children,
  holdOpen = false,
}: {
  readonly children: ReactNode;
  readonly holdOpen?: boolean;
}) {
  const [stored, setCollapsed] = useLocalStorage(homeSidebarSectionStorageKey("more"), true, Schema.Boolean);
  const open = !stored || holdOpen;
  const panelId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  // Focus follows the click: into the first revealed row, or back to More.
  const pendingFocus = useRef<"items" | "more" | null>(null);

  useEffect(() => {
    const target = pendingFocus.current;
    pendingFocus.current = null;
    const root = rootRef.current;
    if (target === null || root === null) return;
    const element =
      target === "items"
        ? root.querySelector<HTMLElement>("[data-home-more-items] :is(a[href], button)")
        : root.querySelector<HTMLElement>("[data-home-more-toggle]");
    element?.focus({ preventScroll: true });
  }, [open]);

  return (
    // The panels clip while they travel, so the rows' focus ring is drawn inside them.
    <div
      ref={rootRef}
      className="flex flex-col [&_[data-slot=sidebar-row]:focus-visible]:ring-inset"
      data-home-sidebar-section="more"
      data-open={open || undefined}
    >
      <Collapsible open={!open}>
        <CollapsiblePanel className={TIMELINE_DISCLOSURE_PANEL_CLASS}>
          <SidebarRow
            data-home-more-toggle=""
            aria-expanded={false}
            aria-controls={panelId}
            onClick={() => {
              pendingFocus.current = "items";
              setCollapsed(false);
            }}
          >
            <EllipsisIcon aria-hidden />
            <span className="truncate">More</span>
          </SidebarRow>
        </CollapsiblePanel>
      </Collapsible>
      <Collapsible open={open}>
        <CollapsiblePanel id={panelId} className={TIMELINE_DISCLOSURE_PANEL_CLASS}>
          <SidebarMenu className="gap-0.5" data-home-more-items="">
            {children}
            {holdOpen ? null : (
              <SidebarMenuItem>
                <SidebarRow
                  data-home-show-less=""
                  aria-expanded
                  aria-controls={panelId}
                  onClick={() => {
                    pendingFocus.current = "more";
                    setCollapsed(true);
                  }}
                >
                  <ChevronUpIcon aria-hidden />
                  <span className="truncate">Show less</span>
                </SidebarRow>
              </SidebarMenuItem>
            )}
          </SidebarMenu>
        </CollapsiblePanel>
      </Collapsible>
    </div>
  );
}
